import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  getActiveLayerVisualizationMeasureSettings,
  patchActiveLayerVisualizationMeasureSettings,
} from '../api/visualizationMeasureSettings';
import { SurveyDataManagerApiError } from '../contracts/common';
import {
  ActiveLayerVisualizationMeasureSettingsPatchBody,
  ActiveLayerVisualizationMeasureSettingsResponse,
} from '../contracts/visualizationMeasureSettings';

import { surveyDataManagerQueryKeys } from './queryKeys';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export function useActiveLayerVisualizationSettings(
  instanceId: string,
  enabled: boolean = true,
): UseQueryResult<ActiveLayerVisualizationMeasureSettingsResponse | null, SurveyDataManagerApiError> {
  return useQuery<ActiveLayerVisualizationMeasureSettingsResponse | null, SurveyDataManagerApiError>({
    queryKey: surveyDataManagerQueryKeys.activeLayerVisualizationSettings(instanceId),
    queryFn: () => getActiveLayerVisualizationMeasureSettings(instanceId),
    staleTime: FIVE_MINUTES_MS,
    enabled: enabled && Boolean(instanceId),
  });
}

export interface PatchActiveLayerVisualizationSettingsInput {
  readonly instanceId: string;
  readonly body: ActiveLayerVisualizationMeasureSettingsPatchBody;
}

export function usePatchActiveLayerVisualizationSettings(): UseMutationResult<
  ActiveLayerVisualizationMeasureSettingsResponse,
  SurveyDataManagerApiError,
  PatchActiveLayerVisualizationSettingsInput
> {
  const queryClient = useQueryClient();

  return useMutation<
    ActiveLayerVisualizationMeasureSettingsResponse,
    SurveyDataManagerApiError,
    PatchActiveLayerVisualizationSettingsInput
  >({
    mutationFn: ({ instanceId, body }) =>
      patchActiveLayerVisualizationMeasureSettings(instanceId, body),
    onSuccess: (data) => {
      queryClient.setQueryData(
        surveyDataManagerQueryKeys.activeLayerVisualizationSettings(data.instanceId),
        data,
      );
    },
  });
}
